/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
// import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductImageCarousel, {
    ProductImageCarouselStyles,
} from "@insite/content-library/Components/ProductImageCarousel";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@insite/content-library/Components/ProductPrimaryImage";
import Badge from "@insite/mobius/Badge";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useOverlayBadge from "@monsma/App/Hooks/useOverlayBadge";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// <XNG-Changes>
const enum fields {
    showOverlayImageBadge = "showOverlayImageBadge",
    showOverlayTextBadge = "showOverlayTextBadge",
    maximumNumberOfOverlayBadges = "maximumNumberOfOverlayBadges",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showOverlayImageBadge]: boolean;
        [fields.showOverlayTextBadge]: boolean;
        [fields.maximumNumberOfOverlayBadges]: number;
    };
}
// </XNG-Changes>

// XNG-Changes - replace WidgetProps with OwnProps
type Props = OwnProps &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState, ownProps: HasProductContext) => ({
    selectedImage: getSelectedImage(state, ownProps.productContext.product),
    selectedImageIndex: state.pages.productDetails.selectedImageIndex,
});

const mapDispatchToProps = {
    setSelectedImageIndex,
};

// xng-change
export interface XngStyles {
    fullPrimaryImageModalContentWrapper?: InjectableCss;
    titleStatisticsAndCarousel?: InjectableCss;
    titleText?: TypographyPresentationProps;
    zoomIconWrapper?: InjectableCss;
    zoomIcon?: IconProps;
    primaryImageWrapper?: InjectableCss;
}

export interface ProductDetailsPrimaryImageStyles {
    /**
     * @deprecated Use primaryImage styles instead.
     */
    centeringWrapper?: InjectableCss;
    hiddenWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    image?: LazyImageProps;

    primaryImage?: ProductPrimaryImageStyles;
    fullPrimaryImageModal?: ModalPresentationProps;
    fullPrimaryImage?: ProductPrimaryImageStyles;
    imageCarousel?: ProductImageCarouselStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    xngStyles?: XngStyles; // xng-change
}

export const primaryImageStyles: ProductDetailsPrimaryImageStyles = {
    fullPrimaryImageModal: {
        cssOverrides: {
            modalContainer: css`
                width: auto;
            `,
            modalBody: css`
                width: auto;
                max-width: initial;
                max-height: initial !important;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 10px 10px 0;
            `,
            modalContent: css`
                text-align: center;
            `,
        },
    },
    fullPrimaryImage: {
        image: {
            css: css`
                img {
                    width: auto;
                    max-height: 70vh;
                }
            `,
        },
        img: {
            css: css`
                width: auto;
                max-height: 70vh;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const styles = primaryImageStyles;

const ProductDetailsPrimaryImage = ({
    productContext,
    selectedImage,
    selectedImageIndex,
    setSelectedImageIndex,
    // XNG-Changes
    fields: { showOverlayTextBadge, showOverlayImageBadge, maximumNumberOfOverlayBadges },
}: Props) => {
    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = useState(false);
    const imageClickHandler = () => {
        setFullImageModalIsOpen(true);
    };
    const fullPrimaryImageModalCloseHandler = () => {
        setFullImageModalIsOpen(false);
    };

    const selectImageHandler = (index: number) => {
        setSelectedImageIndex({ index });
    };
    const { product } = productContext;
    if (!product) {
        return null;
    }

    const image =
        selectedImage ??
        ({
            id: product.id,
            imageAltText: product.imageAltText,
            imageType: "Static",
            largeImagePath: product.largeImagePath,
            mediumImagePath: product.mediumImagePath,
            smallImagePath: product.smallImagePath,
        } as ImageModel);

    const path = image.mediumImagePath || image.largeImagePath;
    if (!path) {
        return null;
    }
    // xng-change: change import
    const { overlayBadges } = useOverlayBadge({
        productContext,
        showTextBadge: showOverlayTextBadge,
        showImageBadge: showOverlayImageBadge,
        maximumNumberOfOverlayBadges,
        styles,
    });

    const showOverlayBadge = overlayBadges.length > 0;
    // xng-change: update the template
    return (
        <>
            {!showOverlayBadge && (
                <StyledWrapper {...styles.xngStyles?.primaryImageWrapper}>
                    <ProductPrimaryImage
                        product={product}
                        image={image}
                        onClick={imageClickHandler}
                        extendedStyles={styles.primaryImage}
                    />
                    <StyledWrapper {...styles.xngStyles?.zoomIconWrapper} onClick={imageClickHandler}>
                        <IconMemo {...styles.xngStyles?.zoomIcon} />
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {/* <XNG-Changes - Remove overlayBadge from condition */}
            {showOverlayBadge && (
                <StyledWrapper {...styles.badgeAreaWrapper}>
                    <ProductPrimaryImage
                        product={product}
                        image={image}
                        onClick={imageClickHandler}
                        extendedStyles={styles.primaryImage}
                    />
                    {/* XNG-Changes - Show the list of badges */}
                    {overlayBadges.map(({ overlayBadge, overlayBadgeWrapperStyles }, index) => (
                        <StyledWrapper key={`${overlayBadge.name}-${index}`} {...overlayBadgeWrapperStyles}>
                            <Badge
                                type={overlayBadge.badgeType}
                                value={
                                    overlayBadge.badgeType === "Image"
                                        ? overlayBadge.largeImageBadgePath
                                        : overlayBadge.displayText
                                }
                                badgeTextColor={overlayBadge.textColorHexCode}
                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                                textBadgeCss={styles.badgeText}
                                imageBadgeProps={styles.badgeImageProps}
                            />
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            )}
            {/* </XNG-Changes> */}
            <Modal
                {...styles.fullPrimaryImageModal}
                isOpen={fullPrimaryImageModalIsOpen}
                handleClose={fullPrimaryImageModalCloseHandler}
            >
                <StyledWrapper {...styles.xngStyles?.fullPrimaryImageModalContentWrapper}>
                    <ProductPrimaryImage
                        product={product}
                        image={image}
                        useLargeImage={true}
                        extendedStyles={styles.fullPrimaryImage}
                    />
                    <StyledWrapper {...styles.xngStyles?.titleStatisticsAndCarousel}>
                        <Typography {...styles.xngStyles?.titleText} data-test-selector="ProductDetailsPageTitle">
                            {product.productTitle}
                        </Typography>
                        {product.images && product.images.length > 1 && (
                            <ProductImageCarousel
                                selectedIndex={selectedImageIndex}
                                onSelectImage={selectImageHandler}
                                extendedStyles={styles.imageCarousel}
                            />
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsPrimaryImage)),
    definition: {
        displayName: "Primary Image",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        // XNG-Changes
        fieldDefinitions: [
            {
                name: fields.showOverlayImageBadge,
                displayName: "Show Overlay Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            },
            {
                name: fields.showOverlayTextBadge,
                displayName: "Show Overlay Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            },
            {
                name: fields.maximumNumberOfOverlayBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showOverlayImageBadge || widget.fields.showOverlayTextBadge,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                ],
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
